import { CardShell } from '@atlas/ui-components'
import { Badge, Skeleton } from '@atlas/ui-primitives'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAxes, useValues } from '@/hooks/useClassification'
import type { AxisDTO } from '@/services/classification.service'
import { FlatValueList } from './FlatValueList'
import { ValueTree } from './ValueTree'

/**
 * One page showing EVERY axis with its full value tree (hierarchical) or list
 * (flat). Rendered dynamically from the registry (design D5). Each value links
 * to its per-system mapping grid. Complements the lighter Systematic View.
 */

function AxisSection({ axis }: { axis: AxisDTO }) {
	const [open, setOpen] = useState(false)
	const { data: values = [], isLoading } = useValues(open ? axis.axisId : null)
	return (
		<CardShell>
			<button
				type="button"
				aria-expanded={open}
				onClick={() => setOpen((o) => !o)}
				className="flex w-full cursor-pointer items-center gap-2 text-left text-base font-semibold"
			>
				<span className="text-ds-text-subtle">{open ? '▾' : '▸'}</span>
				{axis.name}
				<span className="text-xs text-ds-text-subtle">{axis.code}</span>
				<Badge className="ml-1" variant={axis.kind === 'dimension' ? 'primary' : 'secondary'} size="sm">
					{axis.kind}
					{axis.isHierarchical ? ' · tree' : ''}
				</Badge>
			</button>
			{open && (
				<div className="mt-4">
					{isLoading ? (
						<div aria-busy="true">
							<Skeleton shape="text" className="w-40" />
							<span className="sr-only">Loading…</span>
						</div>
					) : axis.isHierarchical ? (
						<ValueTree values={values} />
					) : (
						<FlatValueList values={values} />
					)}
				</div>
			)}
		</CardShell>
	)
}

export function FullTaxonomyPage() {
	const { data: axes = [], isLoading } = useAxes()
	return (
		<div className="space-y-4">
			<div>
				<Link to="/admin/classification" className="text-sm text-ds-primary hover:underline">
					← Axis registry
				</Link>
				<h1 className="text-2xl font-semibold mt-1">Full Taxonomy</h1>
				<p className="text-ds-text-muted">
					{isLoading ? 'Loading…' : `${axes.length} axes — expand any to see its full values & hierarchy`}
				</p>
			</div>
			{axes.map((axis) => (
				<AxisSection key={axis.axisId} axis={axis} />
			))}
		</div>
	)
}
